<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import UserStore from '@/service/auth/UserService';
import TeamService from '@/service/team/TeamService';

const UserService = UserStore();
const router = useRouter();
const emit = defineEmits(['close']);

const teamDataFromApi = ref(null);
const isLoadingTeam = ref(false);

const userInfo = computed(() => UserService.getUserInfo);
const isMainAffiliate = computed(() => userInfo.value?.role === 'affiliate' && !userInfo.value?.owner_affiliate_id);
const isSubAffiliate = computed(() => userInfo.value?.role === 'sub_affiliate');

// Get team name from affiliate profile with fallback to API data
const teamName = computed(() => {
    // Priority 1: Fresh API data
    if (teamDataFromApi.value?.company_name) {
        return teamDataFromApi.value.company_name;
    }
    // Priority 2: Cached bank info
    if (UserService.getUserBankInfo?.company_name) {
        return UserService.getUserBankInfo.company_name;
    }
    // Priority 3: User info company name
    if (userInfo.value?.company_name) {
        return userInfo.value.company_name;
    }
    // Default fallback
    return 'My Team';
});

// Fetch team data
const fetchTeamData = async () => {
    // Only fetch if user is an affiliate (main or sub)
    if (isMainAffiliate.value || isSubAffiliate.value) {
        try {
            isLoadingTeam.value = true;
            const response = await TeamService.getTeam();
            if (response.status === 'success' && response.data) {
                teamDataFromApi.value = response.data;
            }
        } catch (error) {
            console.error('Failed to load team data in UserMenu:', error);
        } finally {
            isLoadingTeam.value = false;
        }
    }
};

// Fetch team data on mount to ensure fresh data
onMounted(() => {
    fetchTeamData();
});

// Watch for user changes (e.g., during impersonation) and refresh team data
watch(() => userInfo.value?.email, (newEmail, oldEmail) => {
    if (newEmail && newEmail !== oldEmail) {
        // User has changed, refresh team data
        teamDataFromApi.value = null; // Clear old data
        fetchTeamData();
    }
}, { immediate: false });

const userInitials = computed(() => {
    const name = userInfo.value?.name || '';
    return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
});

const logOut = () => {
    UserService.logOut();
    router.push({ name: 'login' });
    emit('close');
};

const goToProfile = (hash = 'profile') => {
    router.push({ name: 'profile', hash: `#${hash}` });
    emit('close');
};
</script>

<template>
    <div class="user-menu">
        <!-- User Info Section -->
        <div class="user-menu-header">
            <div class="user-avatar">
                <span class="avatar-text">{{ userInitials }}</span>
            </div>
            <div class="user-details">
                <h4 class="user-name">{{ userInfo?.name }}</h4>
                <p class="user-email">{{ userInfo?.email }}</p>
                <span v-if="isSubAffiliate" class="badge badge-sub">Team Member</span>
                <span v-else-if="isMainAffiliate" class="badge badge-owner">Team Owner</span>
            </div>
        </div>

        <!-- Team Name - Clickable -->
        <div class="team-section" @click="goToProfile('team')">
            <div class="team-info">
                <i class="pi pi-users"></i>
                <div class="team-details">
                    <span class="team-label">Team</span>
                    <span class="team-name">{{ teamName }}</span>
                </div>
            </div>
            <i class="pi pi-chevron-right"></i>
        </div>

        <Divider class="my-0" />

        <!-- Menu Items -->
        <div class="menu-items">
            <div class="menu-item" @click="goToProfile('profile')">
                <i class="pi pi-user"></i>
                <span>My Profile</span>
            </div>
            <div class="menu-item" @click="logOut">
                <i class="pi pi-power-off"></i>
                <span>Logout</span>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.user-menu {
    min-width: 280px;
    padding: 0;
}

.user-menu-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--surface-50);
    border-bottom: 1px solid var(--surface-border);
}

.user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.avatar-text {
    color: white;
    font-size: 1.2rem;
    font-weight: 600;
}

.user-details {
    flex: 1;
    min-width: 0;
}

.user-name {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-email {
    margin: 0.25rem 0 0.5rem;
    font-size: 0.875rem;
    color: var(--text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
}

.badge-owner {
    background: var(--primary-100);
    color: var(--primary-700);
}

.badge-sub {
    background: var(--blue-100);
    color: var(--blue-700);
}

.team-section {
    padding: 1rem 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background: var(--surface-100);
    }
}

.team-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    > i {
        font-size: 1.25rem;
        color: var(--primary-color);
    }
}

.team-details {
    display: flex;
    flex-direction: column;
}

.team-label {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.team-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-color);
}

.menu-items {
    padding: 0.5rem 0;
}

.menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1.5rem;
    cursor: pointer;
    transition: background-color 0.2s;
    color: var(--text-color);

    i {
        font-size: 1.1rem;
        color: var(--text-color-secondary);
    }

    span {
        font-size: 0.95rem;
    }

    &:hover {
        background: var(--surface-100);
    }

    &:last-child {
        i {
            color: var(--red-500);
        }
        &:hover {
            background: var(--red-50);
            color: var(--red-500);
        }
    }
}
</style>

